import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {LandingPageComponent} from './landing-page/landing-page.component';
import {StudentLandingPageComponent} from './landing-page/student-landing-page/student-landing-page.component';
import {studentGuardFn} from './routing-hub/guards/student.guard';
import {SupervisorLandingPageComponent} from "./landing-page/supervisor-landing-page/supervisor-landing-page.component";
import {AdminLandingPageComponent} from "./landing-page/admin-landing-page/admin-landing-page.component";
import {supervisorGuardFn} from "./routing-hub/guards/supervisor.guard";
import {adminGuardFn} from "./routing-hub/guards/admin.guard";
import {adminApprovalGuardFn} from './routing-hub/guards/admin-approval.guard';
import {FormsModule} from "@angular/forms";
import {incomingImpersonateGuardFn} from "./routing-hub/guards/incoming-impersonate.guard";
import {accessRevokedGuardFn} from "./routing-hub/guards/access-revoked.guard";
import {AccessRevokedComponent} from "./feature/access-revoked/access-revoked.component";
import {FailedSubmissionsComponent} from "./feature/failed-submissions/failed-submissions.component";
import {
  PastTermRegistrationFormResolver
} from "./feature/past-term-registration-form/resolvers/past-term-registration-form.resolver";
import {PastTermRegistrationGuard} from "./feature/past-term-registration-form/guards/past-term-registration.guard";
import {SpinnerComponent} from './shared/components/spinner/spinner.component';
import {supervisorContractGuardFn} from "./routing-hub/guards/supervisor-contract.guard";
import {supervisorLandingGuardFn} from './routing-hub/guards/supervisor-landing.guard';
import {rootAreaGuardFn} from "./routing-hub/guards/root-area.guard";
import {ErrorPageComponent} from "./feature/error-page/error-page.component";
import {organizationAccessGuardFn} from './routing-hub/guards/organization-access.guard';

const routes: Routes = [
  {
    path: '',
    title: '',
    component: LandingPageComponent,
    canActivate: [accessRevokedGuardFn],
    children: [
      {
        path: 'access-revoked',
        title: 'Access Revoked',
        component: AccessRevokedComponent,
        pathMatch: 'full',
      },
      {
        path: 'impersonate',
        title: 'Impersonate',
        canActivate: [incomingImpersonateGuardFn],
        pathMatch: 'full',
        children: []
      },
      {
        path: 'impersonate-loading',
        title: 'Impersonation Loading',
        component: SpinnerComponent,
        pathMatch: 'full',
      },
      {
        path: 'error',
        title: 'Error',
        component: ErrorPageComponent,
        pathMatch: 'full',
      },
      {
        path: '',
        title: '',
        component: StudentLandingPageComponent,
        canActivate: [studentGuardFn],
        children: [
          {
            path: '',
            title: '',
            canActivate: [rootAreaGuardFn],
            pathMatch: 'full',
            children: []
          },
          {
            path: 'student-dashboard',
            title: 'Student Dashboard',
            canActivate: [studentGuardFn],
            loadChildren: () => import('./feature/student-dashboard/student-dashboard.module').then(m => m.StudentDashboardModule),
          },
          {
            path: 'opportunity',
            title: 'Opportunity',
            loadChildren: () => import('./feature/opportunity/opportunity.module').then(m => m.OpportunityModule),
          },
          {
            path: 'organization',
            title: 'Organization',
            loadChildren: () => import('./feature/organization/organization.module').then(m => m.OrganizationModule),
          },
          {
            path: 'search-opportunity',
            title: 'Search Opportunity',
            loadChildren: () => import('./feature/search-opportunity/search-opportunity.module').then(m => m.SearchOpportunityModule),
          },
          {
            path: 'register-opportunity/:opportunityId',
            title: 'Register Opportunity',
            loadChildren: () => import('./feature/register-opportunity/register-opportunity.module').then(m => m.RegisterOpportunityModule),
          },
          {
            path: 'evaluation',
            title: 'Evaluation',
            loadChildren: () => import('./feature/student-evaluation/student-evaluation.module').then(m => m.StudentEvaluationModule)
          },
          {
            path: 'audit',
            title: 'Audit',
            loadChildren: () => import('./feature/audit/audit.module').then(m => m.AuditModule)
          },
          {
            path: 'past-term-registration',
            title: 'Past Term Registration Form',
            canActivate: [PastTermRegistrationGuard],
            resolve: { serviceRegistrationData: PastTermRegistrationFormResolver },
            loadChildren: () => import('./feature/past-term-registration-form/past-term-registration-form.module').then(m => m.PastTermRegistrationFormModule)
          }
        ]
      },
      {
        path: 'supervisor',
        title: '',
        component: SupervisorLandingPageComponent,
        canActivate: [supervisorGuardFn, supervisorContractGuardFn],
        children: [
          {
            path: '',
            pathMatch: 'full',
            canActivate: [supervisorLandingGuardFn],
            children: []
          },
          {
            path: 'my-opportunities/:opportunityId',
            title: 'My Opportunities',
            loadChildren: () => import('./feature/opportunity-config/opportunity-config.module').then(m => m.OpportunityConfigModule),
          },
          {
            path: 'edit-profile',
            title: 'Edit Profile',
            loadChildren: () => import('./feature/edit-profile/edit-profile-module').then(m => m.EditProfileModule),
          },
          {
            path: 'my-organizations/:organizationId',
            title: 'My Organizations',
            canActivate: [organizationAccessGuardFn],
            loadChildren: () => import('./feature/organization-config/organization-config.module').then(m => m.OrganizationConfigModule)
          },
          {
            path: 'organization',
            title: 'Organization',
            loadChildren: () => import('./feature/organization/organization.module').then(m => m.OrganizationModule),
          },
          {
            path: 'opportunity',
            title: 'Opportunity',
            loadChildren: () => import('./feature/opportunity/opportunity.module').then(m => m.OpportunityModule),
          },
          {
            path: 'past-opportunities',
            title: 'Past Opportunities',
            data: {
              past: true
            },
            loadChildren: () => import('./feature/past-opportunities/past-opportunities.module').then(m => m.PastOpportunitiesModule),
          },
          {
            path: 'future-opportunities',
            title: 'Future Opportunities',
            data: {
              past: false
            },
            loadChildren: () => import('./feature/past-opportunities/past-opportunities.module').then(m => m.PastOpportunitiesModule),
          },
          {
            path: 'service-evaluation',
            title: 'Service Evaluation',
            loadChildren: () => import('./feature/supervisor-evaluation/supervisor-evaluation.module').then(m => m.SupervisorEvaluationModule)
          },
          {
            path: 'supervisor-no-organization',
            title: 'Supervisor No Organization',
            loadChildren: () => import('./feature/supervisor-no-org/supervisor-no-org.module').then(m => m.SupervisorNoOrgModule),
          },
          {
            path: 'supervisor-contract',
            redirectTo: '/supervisor-contract',
            pathMatch: 'prefix'
          },
          {
            path: 'rollover/:organizationId',
            title: 'Opportunity Rollover',
            canActivate: [organizationAccessGuardFn],
            loadChildren: () => import('./feature/opportunity-rollover/opportunity-rollover.module').then(m => m.OpportunityRolloverModule)
          },
          {
            path: 'past-term-registration-requests',
            title: 'Past Term Registrations',
            canActivate: [PastTermRegistrationGuard],
            loadChildren: () => import('./feature/past-term-registration-requests/past-term-registration-requests.module').then(m => m.PastTermRegistrationRequestsModule),
          },
        ],
      },
      {
        path: 'supervisor-contract',
        title: 'Supervisor Contract',
        canActivate: [supervisorGuardFn],
        loadChildren: () => import('./feature/supervisor-contract/supervisor-contract.module').then(m => m.SupervisorContractModule)
      },
      {
        path: 'admin',
        title: '',
        component: AdminLandingPageComponent,
        canActivate: [adminGuardFn],
        children: [
          {
            path: '',
            title: '',
            redirectTo: 'users',
            pathMatch: 'full'
          },
          {
            path: 'organizations',
            title: 'Organizations',
            loadChildren: () => import('./feature/organization-admin/organization-admin.module').then(m => m.OrganizationAdminModule),
          },
          {
            path: 'supervisors',
            title: 'Supervisors',
            loadChildren: () => import('./feature/supervisor-admin/supervisor-admin.module').then(m => m.SupervisorAdminModule),
          },
          {
            path: 'opportunities',
            title: 'Opportunities',
            loadChildren: () => import('./feature/opportunity-admin/opportunity-admin.module').then(m => m.OpportunityAdminModule),
          },
          {
            path: 'placements',
            title: 'Placements',
            loadChildren: () => import('./feature/placement-admin/placement-admin.module').then(m => m.PlacementAdminModule),
          },
          {
            path: 'users',
            title: 'Users',
            loadChildren: () => import('./feature/user-admin/user-admin.module').then(m => m.UserAdminModule),
          },
          {
            path: 'edit-profile',
            title: 'Edit Profile',
            loadChildren: () => import('./feature/edit-profile/edit-profile-module').then(m => m.EditProfileModule),
          },
          {
            path: 'organization',
            title: 'Organization',
            loadChildren: () => import('./feature/organization/organization.module').then(m => m.OrganizationModule),
          },
          {
            path: 'opportunity',
            title: 'Opportunity',
            loadChildren: () => import('./feature/opportunity/opportunity.module').then(m => m.OpportunityModule),
          },
          {
            path: 'approvals',
            title: 'Approvals',
            canActivate: [adminApprovalGuardFn],
            loadChildren: () => import('./feature/approvals-admin/approvals.admin.module').then(m => m.ApprovalsAdminModule),
          },
          {
            path: 'announcements',
            title: 'Announcements',
            loadChildren: () => import('./feature/announcements-admin/announcements-admin.module').then(m => m.AnnouncementsAdminModule),
          },
          {
            path: 'terms',
            title: 'Terms',
            loadChildren: () => import('./feature/terms-admin/terms-admin.module').then(m => m.TermsAdminModule),
          },
          {
            path: 'failed-submissions',
            title: 'Failed Submissions',
            component: FailedSubmissionsComponent
          },
          {
            path: 'settings',
            title: 'Global Settings',
            loadChildren: () => import('./feature/admin-settings/admin-settings.module').then(m => m.AdminSettingsModule),
          },
        ],
      }
    ]
  },
  {
    path: '**',
    redirectTo: ''
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {}), FormsModule
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {
}
